import clsx from "clsx";
import React, { useState } from "react";

import { Theme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import CollapsedList from "components/CollapsedList";
import Loader from "components/Loader";
import { useRacehorse360Api } from "hooks/api";
import { useAppSelector } from "hooks/redux/useAppSelector";
import DashboardCard from "pages/DashboardPage/DashboardCard";
import DashboardNotificationItem from "./DashboardNotificationItem";
import NotificationModal from "./NotificationModal";

import useStyles from "./styles";

export interface Props {
  className?: string;
}

const INITIAL_ITEMS_COUNT_LIMIT = 3;

const DashboardNotifications = (props: Props) => {
  const { className } = props;
  const classes = useStyles();
  const matchesUpMd = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up("md")
  );

  const [activeNotificationIndex, setActiveNotificationIndex] =
    useState<number>(null);
  const [showNotificationModal, setShowNotificationModal] =
    useState<boolean>(false);

  const selectedFacilityIds = useAppSelector(
    state => state?.queriesConfig?.selectedFacilityIds
  );

  const { usePullNotifications, useMarkNotificationsAsRead } =
    useRacehorse360Api();

  const { mutateAsync: markNotificationsAsReadRequest } =
    useMarkNotificationsAsRead();
  const {
    isLoading,
    data,
    refetch: refetchPullNotifications,
    isFetching
  } = usePullNotifications(
    {
      query: {
        horseFacilityIds: selectedFacilityIds
      },
      getOptions: {
        select: [
          "id",
          "type",
          "creatorRole",
          "eventType",
          "eventDate",
          "body",
          "horse.name",
          "isRead",
          "updatedBy.firstName",
          "updatedBy.lastName",
          "updatedOn"
        ]
      }
    },
    {
      onError: error => console.error(error)
    }
  );
  const notifications: racehorse360.INotification[] = data?.notifications || [];

  const markNotificationsAsRead = (
    notification: racehorse360.INotification
  ) => {
    if (!notification) return;

    if (!notification.isRead) {
      markNotificationsAsReadRequest({
        ids: [notification.id]
      })
        .then(response => {
          refetchPullNotifications();

          return response;
        })
        .catch(error => console.error(error));
    }
  };

  const handleItemClick = notificationIndex => () => {
    setActiveNotificationIndex(notificationIndex);
    setShowNotificationModal(true);
  };

  const handleCloseNotificationModal = () => {
    setShowNotificationModal(false);
    setActiveNotificationIndex(null);
    markNotificationsAsRead(notifications[activeNotificationIndex]);
  };

  const handleNextClick = () => {
    markNotificationsAsRead(notifications[activeNotificationIndex]);
    setActiveNotificationIndex(activeNotificationIndex + 1);
  };

  const handlePreviousClick = () => {
    markNotificationsAsRead(notifications[activeNotificationIndex]);
    setActiveNotificationIndex(activeNotificationIndex - 1);
  };

  const renderNotifications = () => {
    return notifications.map((item, i) => (
      <DashboardNotificationItem
        notification={item}
        key={item.id}
        index={i}
        onClickNotification={handleItemClick(i)}
      />
    ));
  };

  const notificationsTabHeader = (
    <div className={classes.subHeader}>All ({notifications.length})</div>
  );

  return (
    <DashboardCard
      className={className}
      contentClassName={classes.content}
      dataTestId={"notificationsGrid"}
      header={!matchesUpMd && "Notifications"}
      subHeader={notificationsTabHeader}
      initialItemsCount={!matchesUpMd && INITIAL_ITEMS_COUNT_LIMIT}
    >
      <section className={clsx({ [classes.scrollable]: matchesUpMd })}>
        {isFetching && <Loader className={classes.loaderFetching} />}
        {isLoading ? (
          <Loader />
        ) : (
          <CollapsedList minToShow={3} simpleList={matchesUpMd}>
            {renderNotifications()}
          </CollapsedList>
        )}
      </section>

      {showNotificationModal && (
        <NotificationModal
          onClose={handleCloseNotificationModal}
          notification={notifications[activeNotificationIndex]}
          onNextClick={handleNextClick}
          onPreviousClick={handlePreviousClick}
          disableNextButton={
            activeNotificationIndex === notifications.length - 1
          }
          disablePreviousButton={!activeNotificationIndex}
          onMarkNotificationsAsRead={markNotificationsAsRead}
        />
      )}
    </DashboardCard>
  );
};

export default DashboardNotifications;
